import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ReactComponent as RemoveFileIcon } from 'assets/iconButtons/remove-file.svg';
import { convertBytesToMegabytes } from 'services/utils';
import './CustomFileDragAndDrop.scss';

const CustomFileDragAndDrop = ({
  files,
  onChange,
  onRemoveFiles,
  onError,
  maxSize = 15 * 1024 * 1024, // 15MB in bytes
  accept = '.pdf,.jpg,.jpeg,.png',
  isDisabled = false,
  error
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [hoveredFileId, setHoveredFileId] = useState(null);
  const [localError, setLocalError] = useState(null);
  const fileInputRef = useRef(null);
  const createdUrlsRef = useRef(new Map());

  // Cleanup created URLs on unmount
  useEffect(() => {
    return () => {
      createdUrlsRef.current.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // Ignore errors when revoking URLs
        }
      });
    };
  }, []);

  // Clear local error after 5 seconds
  useEffect(() => {
    if (localError) {
      const timer = setTimeout(() => {
        setLocalError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [localError]);

  const createAndTrackUrl = (file, fileId) => {
    // Check if we already have a URL for this file
    if (createdUrlsRef.current.has(fileId)) {
      return createdUrlsRef.current.get(fileId);
    }

    const url = URL.createObjectURL(file);
    createdUrlsRef.current.set(fileId, url);
    return url;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!isDisabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    if (!isDisabled) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      handleFiles(droppedFiles);
    }
  };

  const handleFiles = (filesList) => {
    const oversizedFiles = [];
    const invalidFormatFiles = [];
    const validFiles = [];

    filesList.forEach((file) => {
      const extension = `.${file.name.split('.').pop().toLowerCase()}`;
      const acceptedExtensions = accept.split(',').map((ext) => ext.trim().toLowerCase());

      if (!acceptedExtensions.includes(extension)) {
        invalidFormatFiles.push(file);
      } else if (file.size > maxSize) {
        oversizedFiles.push(file);
      } else {
        validFiles.push(file);
      }
    });

    // Show error for oversized files
    if (oversizedFiles.length > 0) {
      const maxSizeMB = convertBytesToMegabytes(maxSize);
      const errorMsg = `File size exceeds ${maxSizeMB}MB. Please select a smaller file.`;
      setLocalError(errorMsg);

      // Also call the external error handler if provided
      if (onError) {
        onError('REQUEST_HAS_BEEN_TERMINATED');
      }

      // Don't process any files if there are oversized ones
      // Reset input value to allow re-selecting files
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Show error for invalid format files
    if (invalidFormatFiles.length > 0) {
      const errorMsg = `Invalid file format. Allowed formats: ${accept}`;
      setLocalError(errorMsg);

      // Reset input value to allow re-selecting files
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Clear error if valid files are being processed
    setLocalError(null);

    if (validFiles.length > 0) {
      // Create a fake event object similar to what AttachDoc expects
      const fakeEvent = {
        target: {
          files: validFiles
        }
      };
      onChange(fakeEvent);
    }

    // Reset input value to allow re-selecting the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
  };

  const handleBrowseClick = () => {
    if (!isDisabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 KB';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFilePreview = (file) => {
    // Check if file is an image by type or extension
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    const pdfExtensions = ['pdf'];
    const extension = file.name ? file.name.split('.').pop().toLowerCase() : '';

    // Use originalFile if it exists (for uploaded files from server)
    const fileToPreview = file.originalFile || file;

    const isImage =
      (fileToPreview.type && fileToPreview.type.startsWith('image/')) ||
      (file.type && file.type.startsWith('image/')) ||
      imageExtensions.includes(extension);

    const isPdf =
      (fileToPreview.type && fileToPreview.type === 'application/pdf') ||
      (file.type && file.type === 'application/pdf') ||
      pdfExtensions.includes(extension);

    if (isImage) {
      // If we have an originalFile property, use it
      if (file.originalFile && file.originalFile instanceof File) {
        return { url: createAndTrackUrl(file.originalFile, file.id), type: 'image' };
      }
      // If file has a URL property (already uploaded), use it
      if (file.url) {
        return { url: file.url, type: 'image' };
      }
      // If it's a File object, create a blob URL
      if (file instanceof File) {
        return { url: createAndTrackUrl(file, file.id || file.name), type: 'image' };
      }
      // If file has a preview property
      if (file.preview) {
        return { url: file.preview, type: 'image' };
      }
      // Try to use the file directly if it's a Blob
      if (file instanceof Blob) {
        return { url: createAndTrackUrl(file, file.id || 'blob'), type: 'image' };
      }
    }

    if (isPdf) {
      // If we have an originalFile property, use it
      if (file.originalFile && file.originalFile instanceof File) {
        return { url: createAndTrackUrl(file.originalFile, file.id), type: 'pdf' };
      }
      // If file has a URL property (already uploaded), use it
      if (file.url) {
        return { url: file.url, type: 'pdf' };
      }
      // If it's a File object, create a blob URL
      if (file instanceof File) {
        return { url: createAndTrackUrl(file, file.id || file.name), type: 'pdf' };
      }
      // If file has a preview property
      if (file.preview) {
        return { url: file.preview, type: 'pdf' };
      }
      // Try to use the file directly if it's a Blob
      if (file instanceof Blob) {
        return { url: createAndTrackUrl(file, file.id || 'blob'), type: 'pdf' };
      }

      // For PDFs without URL, just return type indicator
      return { type: 'pdf' };
    }

    return null;
  };

  const truncateFileName = (name, maxLength = 15) => {
    if (!name) return '';
    const extension = name.split('.').pop();
    const nameWithoutExt = name.substring(0, name.lastIndexOf('.'));

    if (name.length <= maxLength) return name;

    const truncatedName = nameWithoutExt.substring(0, 8) + '...' + nameWithoutExt.substring(nameWithoutExt.length - 4);
    return `${truncatedName}.${extension}`;
  };

  return (
    <div className='custom-file-drag-drop'>
      <div
        className={`dropzone ${isDragging ? 'dragging' : ''} ${isDisabled ? 'disabled' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type='file'
          multiple
          accept={accept}
          onChange={handleFileSelect}
          disabled={isDisabled}
          style={{ display: 'none' }}
        />

        <div className='dropzone-content'>
          <div className='dropzone-text'>
            <span className='drop-text'>Drop file or </span>
            <span
              className='browse-text'
              onClick={handleBrowseClick}
              onKeyDown={(e) => e.key === 'Enter' && handleBrowseClick()}
              role='button'
              tabIndex={0}
            >
              Browse
            </span>
          </div>
          <div className='file-types'>.PDF, .JPG, .JPEG, .PNG and max size {convertBytesToMegabytes(maxSize)}MB</div>
        </div>
      </div>

      {(error || localError) && <div className='error-message'>{error || localError}</div>}

      {files && files.length > 0 && (
        <div className='files-preview-wrapper'>
          <div className='files-preview'>
            {files.map((file) => {
              const preview = getFilePreview(file);
              const extension = file.name ? file.name.split('.').pop().toLowerCase() : '';

              return (
                <div
                  key={file.id}
                  className='file-card'
                  onMouseEnter={() => setHoveredFileId(file.id)}
                  onMouseLeave={() => setHoveredFileId(null)}
                >
                  {preview && preview.type === 'pdf' && preview.url ? (
                    <embed className='file-pdf-preview' src={preview.url} type='application/pdf' />
                  ) : preview && preview.type === 'pdf' ? (
                    <div className='file-pdf-preview'>
                      <svg
                        className='pdf-icon'
                        width='36'
                        height='36'
                        viewBox='0 0 24 24'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z'
                          stroke='#8F27C7'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M14 2V8H20'
                          stroke='#8F27C7'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <text
                          x='12'
                          y='16'
                          fontSize='7'
                          fill='#8F27C7'
                          textAnchor='middle'
                          fontWeight='600'
                          fontFamily='Arial, sans-serif'
                        >
                          PDF
                        </text>
                      </svg>
                    </div>
                  ) : preview && preview.type === 'image' ? (
                    <img src={preview.url} alt={file.name} className='file-image-background' />
                  ) : (
                    <div className='file-default-background'>
                      <div className='file-type-icon'>{extension.toUpperCase()}</div>
                    </div>
                  )}

                  <div className={`file-overlay ${hoveredFileId === file.id ? 'hovered' : ''}`}>
                    {hoveredFileId === file.id && (
                      <>
                        <div className='file-info'>
                          <span className='file-name'>
                            {truncateFileName(file.name)} ({formatFileSize(file.size)})
                          </span>
                        </div>
                        <div className='file-actions'>
                          <button
                            className='file-action-btn'
                            onClick={(e) => {
                              e.stopPropagation();
                              onRemoveFiles(file.id);
                            }}
                            title='Delete file'
                            type={'button'}
                          >
                            <RemoveFileIcon />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

CustomFileDragAndDrop.propTypes = {
  files: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  onRemoveFiles: PropTypes.func.isRequired,
  onError: PropTypes.func,
  maxSize: PropTypes.number,
  accept: PropTypes.string,
  isDisabled: PropTypes.bool,
  error: PropTypes.string
};

export default CustomFileDragAndDrop;
